import React, { useCallback, useMemo, useState } from "react";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../../component/buttonComponent/ButtonComponent";
import CreateDeeplinkModal from "../../../component/modal/CreateDeeplinkModal";
import EditDeeplinkModal from "../../../component/modal/EditDeeplinkModal";
import SnackBar from "../../../component/Snackbar/Snackbar";
import SearchInput from "../../../component/searchInput/SearchInput";
import LoadingSpinner from "../../../component/loading/LoadingSpinner";
import { useAuth } from "../../../config/utilities/useAuth";
import { useMasterAdmin } from "../MasterAdminContext";
import { APIGetMasterAdminDeeplinks } from "../../../config/utilities/APIs/masterAdmin/deeplinksApi";
import "./MasterAdminInstitutions.css";
import "./MasterAdminDeeplinks.css";

const getDeeplinkId = (deeplink) =>
  deeplink?.deeplinkId || deeplink?._id || "";

const normalizeInstitutionIds = (ids) =>
  (Array.isArray(ids) ? ids : [])
    .filter((id) => id != null && id !== "")
    .map((id) => String(id));

const MAX_VISIBLE_INSTITUTION_TAGS = 3;

const MasterAdminDeeplinks = () => {
  const { token } = useAuth();
  const { institutions } = useMasterAdmin();
  const [deeplinks, setDeeplinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDeeplink, setEditingDeeplink] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");

  const institutionNameById = useMemo(() => {
    const map = new Map();
    institutions.forEach((institution) => {
      map.set(institution.institutionId, institution.institutionName);
    });
    return map;
  }, [institutions]);

  const fetchDeeplinks = useCallback(async () => {
    if (!token) return;

    const response = await APIGetMasterAdminDeeplinks(setLoading, token);
    if (response?.status === 200) {
      const items = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setDeeplinks(items);
    }
  }, [token]);

  React.useEffect(() => {
    fetchDeeplinks();
  }, [fetchDeeplinks]);

  const filteredDeeplinks = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return deeplinks;

    return deeplinks.filter((deeplink) => {
      const label = deeplink.label?.toLowerCase() || "";
      const url = deeplink.url?.toLowerCase() || "";
      const institutionNames = normalizeInstitutionIds(deeplink.institutionIds)
        .map((id) => {
          const name = institutionNameById.get(id) ?? id;
          return String(name).toLowerCase();
        })
        .join(" ");
      return (
        label.includes(query) ||
        url.includes(query) ||
        institutionNames.includes(query)
      );
    });
  }, [deeplinks, institutionNameById, search]);

  const showSuccess = (title, subtitle) => {
    setSnackbarType("success");
    setSnackbarTitle(title);
    setSnackbarSubtitle(subtitle);
    setShowSnackbar(true);
  };

  const showError = (title, message) => {
    setSnackbarType("error");
    setSnackbarTitle(title);
    setSnackbarSubtitle(message);
    setShowSnackbar(true);
  };

  const handleOpenEdit = (deeplink) => {
    setEditingDeeplink(deeplink);
    setShowEditModal(true);
  };

  return (
    <div className="ma-deeplinks">
      <div className="ma-deeplinks-toolbar">
        <div className="ma-deeplinks-toolbar-left">
          <SearchInput
            containerWidth="22rem"
            containerHeight="2.5rem"
            text="Search by label, URL, or institution..."
            value={search}
            onChangeText={(e) => setSearch(e.target.value)}
          />
          <span className="ma-deeplinks-count">
            {filteredDeeplinks.length} deeplink
            {filteredDeeplinks.length === 1 ? "" : "s"}
          </span>
        </div>
        <PrimaryButton
          label="Create Deeplink"
          onClickFunction={() => setShowCreateModal(true)}
        />
      </div>

      {loading && deeplinks.length === 0 ? (
        <div className="ma-deeplinks-loading">
          <LoadingSpinner size="large" color="#ffb502" />
        </div>
      ) : filteredDeeplinks.length === 0 ? (
        <div className="ma-deeplinks-empty">
          <h3>No deeplinks found</h3>
          <p>
            {search
              ? "Try a different search term or clear the filter."
              : "Create deeplinks to add custom sidebar links for institution workspaces."}
          </p>
          {!search && (
            <PrimaryButton
              label="Create Deeplink"
              onClickFunction={() => setShowCreateModal(true)}
            />
          )}
        </div>
      ) : (
        <div className="ma-institutions-grid">
          {filteredDeeplinks.map((deeplink) => {
            const assignedInstitutions = normalizeInstitutionIds(
              deeplink.institutionIds
            ).map((id) => ({
              id,
              name: institutionNameById.get(id) || id,
            }));
            const visibleInstitutions = assignedInstitutions.slice(
              0,
              MAX_VISIBLE_INSTITUTION_TAGS
            );
            const hiddenInstitutionCount =
              assignedInstitutions.length - visibleInstitutions.length;
            const hiddenInstitutionNames = assignedInstitutions
              .slice(MAX_VISIBLE_INSTITUTION_TAGS)
              .map((institution) => institution.name)
              .join(", ");

            return (
              <article
                key={getDeeplinkId(deeplink)}
                className="ma-institution-card ma-deeplink-card"
              >
                <div className="ma-institution-card-header">
                  <div className="ma-institution-card-title-wrap">
                    <h3 className="ma-institution-card-title">{deeplink.label}</h3>
                  </div>
                  <SecondaryButton
                    width="4.25rem"
                    label="Edit"
                    onClickFunction={() => handleOpenEdit(deeplink)}
                  />
                </div>

                <div className="ma-institution-card-meta">
                  <span className="ma-institution-meta-label">URL</span>
                  <span className="ma-institution-meta-value">{deeplink.url}</span>
                </div>

                <div className="ma-deeplink-card-footer">
                  <div className="ma-institution-card-tags ma-deeplink-card-tags">
                    {assignedInstitutions.length > 0 ? (
                      <>
                        {visibleInstitutions.map((institution) => (
                          <span key={institution.id} className="ma-institution-tag">
                            {institution.name}
                          </span>
                        ))}
                        {hiddenInstitutionCount > 0 ? (
                          <span
                            className="ma-institution-tag ma-deeplink-tag-more"
                            title={hiddenInstitutionNames}
                          >
                            +{hiddenInstitutionCount}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <span className="ma-institution-tag ma-institution-tag--muted">
                        No institutions assigned
                      </span>
                    )}
                  </div>

                  <div className="ma-institution-card-stats ma-deeplink-card-stats">
                    <div className="ma-institution-stat">
                      <span className="ma-institution-stat-value">
                        {assignedInstitutions.length}
                      </span>
                      <span className="ma-institution-stat-label">
                        Linked institutions
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <CreateDeeplinkModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        institutions={institutions}
        onCreated={() => {
          fetchDeeplinks();
          showSuccess("Deeplink created", "The deeplink has been added.");
        }}
        onError={(message) => showError("Create failed", message)}
      />

      <EditDeeplinkModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingDeeplink(null);
        }}
        deeplink={editingDeeplink}
        institutions={institutions}
        onSave={() =>
          showSuccess("Deeplink updated", "Changes have been saved.")
        }
        onDeleted={() => {
          setShowEditModal(false);
          setEditingDeeplink(null);
          showSuccess("Deeplink deleted", "The deeplink has been removed.");
        }}
        onDeleteError={(message) => showError("Delete failed", message)}
        onPatchError={(message) => showError("Update failed", message)}
        fetchDeeplinks={fetchDeeplinks}
      />

      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </div>
  );
};

export default MasterAdminDeeplinks;
